// Dashboard.js – Sidebar‑less Chakra UI dashboard
import React from "react";
import {
  Box,
  Flex,
  SimpleGrid,
  Heading,
  Text,
  HStack,
  VStack,
  Badge,
  Stat,
  StatNumber,
  useColorModeValue,
  Button,
  Link,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import {
  DollarSign,
  ShoppingCart,
  Users,
  Send,
  Target,
  MessageSquare,
  Mail,
  Phone,
  BarChart2,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import PrimaryButton from "../../components/themeComponents/PrimaryButton";

const STATS = [
  { title: "Revenue by Orbis", value: "8,32,012", icon: DollarSign, trend: 12.4, gradient: "linear(to-r, purple.500, purple.600)" },
  { title: "Total Orders", value: "8,452", icon: ShoppingCart, trend: 8.9, gradient: "linear(to-r, cyan.500, cyan.600)" },
  { title: "Total Users", value: "24,781", icon: Users, trend: 9.7, gradient: "linear(to-r, teal.500, green.400)" },
  { title: "Messages Sent", value: "4,02,982", icon: Send, trend: 15.8, gradient: "linear(to-r, orange.500, red.500)" },
  { title: "Avg. Order Value", value: "₹2,480", icon: Target, trend: 6.2, gradient: "linear(to-r, teal.500, cyan.600)" },
];

const REVENUE_BREAKDOWN = [
  { name: "Flows", value: 39283982, color: "#06b6d4" },
  { name: "Campaigns", value: 28498374, color: "#3b82f6" },
];

const TOP_CAMPAIGNS = [
  { name: "BUY 2 GET 1 FREE", value: 2389283, color: "#8b5cf6" },
  { name: "EXTRA 20% OFF", value: 2389283, color: "#06b6d4" },
  { name: "DIWALI SALE 2024", value: 2738723, color: "#3b82f6" },
];

const PLATFORMS = [
  { name: "Email", icon: Mail, status: "live", partner: "SendGrid", lastActivity: "2 min ago" },
  { name: "SMS", icon: Phone, status: "live", partner: "Twilio", lastActivity: "10 min ago" },
  { name: "WhatsApp", icon: MessageSquare, status: "warning", partner: "Meta Business", lastActivity: "1 h ago" },
];

const StatCard = ({ title, value, icon: Icon, trend, gradient }) => {
  const bg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.100", "gray.700");
  const trendColor = trend >= 0 ? "green.500" : "red.500";

  return (
    <Stat p={6} bg={bg} rounded="xl" border="1px" borderColor={border} shadow="sm" _hover={{ shadow: "md" }}>
      <VStack spacing={3} mb={3} align="center">
        <Flex w={12} h={12} rounded="full" align="center" justify="center" bgGradient={gradient}>
          <Icon size={20} color="white" />
        </Flex>
        <Text fontSize="sm" color="gray.600" textAlign="center"> {title} </Text>
      </VStack>
      <StatNumber fontSize="2xl" fontWeight="bold" textAlign="center"> {value} </StatNumber>
      <Text fontSize="sm" fontWeight="medium" color={trendColor} textAlign="center"> {trend > 0 ? "+" : ""}{trend}% from last month </Text>
    </Stat>
  );
};

const PieCard = ({ title, data, legend = false }) => {
  const bg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.100", "gray.700");
  return (
    <Box p={6} bg={bg} rounded="xl" border="1px" borderColor={border} shadow="sm">
      <Heading as="h3" size="md" mb={6}> {title} </Heading>
      <Box h="260px">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
              {data.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
            </Pie>
            <Tooltip formatter={(v) => v.toLocaleString()} />
            {legend && <Legend />}
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

const PlatformCard = () => {
  const bg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.100", "gray.700");
  return (
    <Box p={6} bg={bg} rounded="xl" border="1px" borderColor={border} shadow="sm">
      <Heading as="h3" size="md" mb={6}> Platform Status </Heading>
      <VStack align="stretch" spacing={4}>
        {PLATFORMS.map((p) => (
          <HStack key={p.name} justify="space-between">
            <HStack spacing={3}>
              <p.icon size={20} />
              <Box>
                <Text fontWeight="medium"> {p.name} </Text>
                <Text fontSize="sm" color="gray.500"> {p.partner} </Text>
              </Box>
            </HStack>
            <HStack>
              <Badge colorScheme={p.status === "live" ? "green" : "yellow"}>{p.status === "live" ? "Live" : "Warning"}</Badge>
              <Text fontSize="sm" color="gray.500"> {p.lastActivity} </Text>
            </HStack>
          </HStack>
        ))}
      </VStack>
    </Box>
  );
};

function DemoDashboard() {
  const pageBg = useColorModeValue("gray.50", "gray.900");
  return (
    <Box bg={pageBg} minH="100vh">
      <Flex justify="space-between" align="center" px={{ base: 4, md: 8 }} py={6} mb={6}>
        <Box>
          <Heading size="lg" mb={1}>Overview Dashboard</Heading>
          <Text fontSize="sm" color="gray.500">
            High-level summary of your sales, marketing, and communication performance
          </Text>
        </Box>
        <Link as={RouterLink} to="/dashboard/details/analytics">
          <PrimaryButton size="sm" colorScheme="blue" leftIcon={<BarChart2 size={16}  />} label={'Explore Analytics'} ></PrimaryButton>
        </Link>
      </Flex>

      <Box px={{ base: 4, md: 8 }} pb={6}>
        {/* Metrics */}
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 5 }} spacing={6} mb={10}>
          {STATS.map((s) => (
            <StatCard key={s.title} {...s} />
          ))}
        </SimpleGrid>

        {/* Charts */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} mb={10}>
          <PieCard title="Revenue Breakdown" data={REVENUE_BREAKDOWN} />
          <PieCard title="Top Campaigns" data={TOP_CAMPAIGNS} legend={true} />
        </SimpleGrid>

        {/* Platform status */}
        <PlatformCard />
      </Box>
    </Box>
  );
}

export default DemoDashboard;
